import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { appeler } from '../api/client.ts';
import { useSession } from '../composants/session.tsx';
import { Entete } from '../composants/mise-en-page.tsx';
import {
  Carte,
  Chargement,
  DateHeure,
  Erreur,
  Etiquette,
  Vide,
  formaterDateHeure,
} from '../composants/communs.tsx';
import { BoutonImprimer, exporterTableauPdf } from '../composants/ui/index.ts';
import { useApercuPdf } from '../composants/apercu-pdf.tsx';

/**
 * Journal d'audit — RG-147 à RG-159.
 *
 * Lecture seule, paginée par CURSEUR et non par page numérotée : le journal
 * ne fait que croître, et un décalage obligerait la base à parcourir puis
 * jeter tout ce qui précède.
 */

interface Entree {
  sequence: number;
  id: string;
  boutique: string | null;
  type_action: string;
  entite: string | null;
  entite_numero: string | null;
  role_code: string | null;
  motif: string | null;
  horodatage_serveur: string;
  ecart_horloge_s: number | null;
  hash_courant: string;
}

interface Page {
  donnees: Entree[];
  /**
   * Identifiant de la derniere entree lue, et non sa sequence.
   *
   * La sequence est propre a chaque chaine (boutique, installation) : elle
   * ne peut pas ordonner deux boutiques entre elles. L'identifiant est un
   * UUIDv7, donc chronologique par construction.
   */
  curseur_suivant: string | null;
}

/** Forme rendue par `GET /audit/verification` — chainage.ts. */
interface Verification {
  conforme: boolean;
  entreesVerifiees: number;
  premiereAnomalie: { sequence: number; type: string } | null;
  portee_de_la_preuve: string;
}

export function Journal(): ReactNode {
  const { ouvrir } = useApercuPdf();
  const { boutiqueId, peut } = useSession();
  const [curseurs, setCurseurs] = useState<string[]>([]);
  const [typeAction, setTypeAction] = useState('');
  const [verification, setVerification] = useState<Verification | null>(null);

  // En vue consolidee, chaque ligne doit dire de quelle boutique elle
  // vient : sans cette colonne, la liste melange sans le dire.
  const consolide = boutiqueId === null;

  const curseur = curseurs.at(-1);

  const { data, isPending, error } = useQuery({
    queryKey: ['journal', boutiqueId, curseur, typeAction],
    queryFn: () =>
      appeler<Page>(
        `/audit/journal?limite=50` +
          (boutiqueId === null ? '' : `&boutique_id=${boutiqueId}`) +
          (curseur === undefined ? '' : `&curseur=${curseur}`) +
          (typeAction.trim() === ''
            ? ''
            : `&type_action=${encodeURIComponent(typeAction.trim())}`),
      ),
  });

  const { refetch: verifier, isFetching: verificationEnCours } = useQuery({
    queryKey: ['verification', boutiqueId],
    enabled: false,
    queryFn: () =>
      appeler<Verification>(`/audit/verification?boutique_id=${String(boutiqueId)}`),
  });

  return (
    <>
      <Entete
        titre="Journal d’audit"
        sous="Écriture seule. Aucune entrée ne peut être modifiée ni supprimée."
        actions={
          peut('audit.verifier') ? (
            <button
              type="button"
              disabled={verificationEnCours}
              onClick={() => {
                void verifier().then((resultat) => {
                  setVerification(resultat.data ?? null);
                });
              }}
            >
              {verificationEnCours ? 'Vérification…' : 'Vérifier la chaîne'}
            </button>
          ) : undefined
        }
      />

      <Erreur erreur={error} />

      <div className="pile">
        {verification !== null && (
          <Carte titre="Vérification d’intégrité">
            <div className="ligne" style={{ marginBottom: 10 }}>
              {verification.conforme ? (
                <Etiquette ton="vert" texte="chaîne cohérente" />
              ) : (
                <Etiquette ton="rouge" texte="chaîne incohérente" />
              )}
              <span className="entete__sous">
                {verification.entreesVerifiees} entrée(s) vérifiée(s)
              </span>
            </div>

            {/* Une rupture se localise : sans sa séquence, elle est
                inexploitable. */}
            {verification.premiereAnomalie !== null && (
              <div className="message message--erreur">
                Première anomalie à la séquence{' '}
                <span className="mono">
                  {verification.premiereAnomalie.sequence}
                </span>{' '}
                — {verification.premiereAnomalie.type.replace(/_/g, ' ').toLowerCase()}.
              </div>
            )}
            {/*
              Ce que la vérification NE prouve pas est aussi important que ce
              qu'elle prouve. On affiche l'avertissement du serveur tel quel :
              personne ne doit prendre « chaîne cohérente » pour une preuve
              opposable en soi.
            */}
            <div className="message message--attention">
              {verification.portee_de_la_preuve}
            </div>
          </Carte>
        )}

        <Carte>
          <div className="champ" style={{ maxWidth: 320, marginBottom: 12 }}>
            <label htmlFor="type-action">Type d’action</label>
            <input
              id="type-action"
              placeholder="VENTE_ENREGISTREE, PRIX_MODIFIE…"
              value={typeAction}
              onChange={(evenement) => {
                setTypeAction(evenement.target.value.toUpperCase());
                setCurseurs([]);
              }}
            />
          </div>

          {isPending ? (
            <Chargement />
          ) : data === undefined || data.donnees.length === 0 ? (
            <Vide texte="Aucune entrée pour ce filtre." />
          ) : (
            <>
              <div className="ligne" style={{ justifyContent: 'flex-end', marginBottom: 8 }}>
                <BoutonImprimer
                  onClick={() => {
                    ouvrir(() =>
                      exporterTableauPdf({
                        titre: 'Journal d’audit',
                        sousTitre: 'Page affichée uniquement',
                        nomFichier: 'journal-audit',
                        colonnes: [
                          {
                            cle: 'sequence',
                            entete: 'Séq.',
                            numerique: true,
                            rendu: () => null,
                            texte: (e: Entree) => String(e.sequence),
                          },
                          ...(consolide
                            ? [{ cle: 'boutique', entete: 'Boutique', rendu: () => null, texte: (e: Entree) => e.boutique ?? '—' }]
                            : []),
                          {
                            cle: 'horodatage',
                            entete: 'Horodatage',
                            rendu: () => null,
                            texte: (e: Entree) => formaterDateHeure(e.horodatage_serveur),
                          },
                          { cle: 'action', entete: 'Action', rendu: () => null, texte: (e: Entree) => e.type_action },
                          {
                            cle: 'entite',
                            entete: 'Entité',
                            rendu: () => null,
                            texte: (e: Entree) => e.entite_numero ?? e.entite ?? '—',
                          },
                          { cle: 'role', entete: 'Rôle', rendu: () => null, texte: (e: Entree) => e.role_code ?? '—' },
                          { cle: 'motif', entete: 'Motif', rendu: () => null, texte: (e: Entree) => e.motif ?? '—' },
                          {
                            cle: 'empreinte',
                            entete: 'Empreinte',
                            rendu: () => null,
                            texte: (e: Entree) => e.hash_courant.slice(0, 10),
                          },
                        ],
                        lignes: data.donnees,
                      }),
                    );
                  }}
                />
              </div>
              <div className="tableau-defilant">
                <table>
                  <thead>
                    <tr>
                      <th>Séq.</th>
                      {consolide && <th>Boutique</th>}
                      <th>Horodatage</th>
                      <th>Action</th>
                      <th>Entité</th>
                      <th>Rôle</th>
                      <th>Motif</th>
                      <th>Empreinte</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.donnees.map((entree) => (
                      <tr key={entree.id}>
                        <td className="mono">{entree.sequence}</td>
                        {consolide && (
                          <td className="mono">{entree.boutique ?? '—'}</td>
                        )}
                        <td>
                          <DateHeure valeur={entree.horodatage_serveur} />
                          {/* Un écart d'horloge important est un signal :
                              l'appareil a peut-être antidaté (RG-137). */}
                          {entree.ecart_horloge_s !== null &&
                            Math.abs(entree.ecart_horloge_s) > 300 && (
                              <div>
                                <Etiquette
                                  ton="ambre"
                                  texte={`écart ${String(entree.ecart_horloge_s)} s`}
                                />
                              </div>
                            )}
                        </td>
                        <td className="mono">{entree.type_action}</td>
                        <td>
                          {entree.entite_numero ?? entree.entite ?? '—'}
                        </td>
                        <td className="mono">{entree.role_code ?? '—'}</td>
                        <td>{entree.motif ?? '—'}</td>
                        <td className="mono" title={entree.hash_courant}>
                          {entree.hash_courant.slice(0, 10)}…
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="ligne" style={{ marginTop: 14 }}>
                <button
                  type="button"
                  className="bouton--discret"
                  disabled={curseurs.length === 0}
                  onClick={() => {
                    setCurseurs((pile) => pile.slice(0, -1));
                  }}
                >
                  Précédent
                </button>
                <button
                  type="button"
                  className="bouton--discret"
                  disabled={data.curseur_suivant === null}
                  onClick={() => {
                    const suivant = data.curseur_suivant;
                    if (suivant !== null) {
                      setCurseurs((pile) => [...pile, suivant]);
                    }
                  }}
                >
                  Suivant
                </button>
              </div>
            </>
          )}
        </Carte>
      </div>
    </>
  );
}
